import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { withRouter, Link } from 'react-router-dom'
import queryString from 'query-string'
import { mediaQueries, withDevice } from '@blazar-plugins/device'
import { numberGroup } from 'utils/format'
import { trackEvent } from '@blocket/tracking'
import { IconLeft, IconRight, TextBody, IconReload, TextCallout2 } from 'blocket-ui'
import map from 'lodash/map'

const DisabledLink = styled.span`
  pointer-events: none;
`

function GenericLink({ to, children, ...props }) {
  if (to) {
    return (
      <Link to={to} {...props}>
        {children}
      </Link>
    )
  }

  return <DisabledLink {...props}>{children}</DisabledLink>
}

GenericLink.propTypes = {
  to: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  children: PropTypes.node.isRequired,
  selected: PropTypes.bool,
}

export const Button = styled(GenericLink)`
  width: 47px;
  min-width: 47px;
  height: 44px;
  border: 1px solid #c2c2c1;
  border-radius: 4px;

  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;

  background: ${(props) => (props.selected ? '#4182c3' : '#fff')};
  color: ${(props) => (props.selected ? '#fff' : '#000')};
  border: ${(props) => (props.selected ? 'none' : '1px solid #c2c2c1')};
`

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  flex-wrap: nowrap;
`

const RefreshWrapper = styled.div`
  padding-top: 40px;
  display: flex;
  justify-content: center;
`

const sharedButtonStyles = css`
  width: auto;
  padding: 0 1em;
`

const RefreshButton = styled(Button)`
  ${sharedButtonStyles}
  height: 40px;
`

export const Buttons = styled(TextBody)`
  display: flex;

  ${Button} {
    border-radius: 0;
    border-right-width: 0;

    &:first-child {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }

    &:last-child {
      border-right-width: 1px;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }
`

const Strong = styled.span`
  font-family: 'BlocketSans-Medium', sans-serif;
`

export const PrevNextButton = styled(Button)`
  @media ${mediaQueries.large} {
    ${sharedButtonStyles}
  }
`

const HelpLabel = styled(TextCallout2)`
  display: none;
  margin-left: 0.5em;
  margin-right: 0.5em;

  @media ${mediaQueries.large} {
    display: inline;
  }
`

export function paginationWindow(currentPage, totalPageCount, numButtons) {
  const halfWindowSize = (numButtons - 1) / 2
  const start = currentPage - Math.ceil(halfWindowSize) - 1
  const end = currentPage + Math.floor(halfWindowSize)

  switch (true) {
    case start < 0:
      return [0, Math.min(end - start, numButtons)]
    case end > totalPageCount:
      return [Math.max(totalPageCount + start - end, 0), totalPageCount]
    default:
      return [start, end]
  }
}

function pagination(currentPage, pageCount) {
  const delta = 3
  const left = currentPage - delta
  const right = currentPage + delta + 1
  let result = []

  const pageCountArray = Array.from({ length: pageCount }, (v, k) => k + 1)

  if (currentPage < 6) {
    result = pageCountArray.filter((i) => i && i <= 8)
  } else if (currentPage > pageCount - 6) {
    result = pageCountArray.filter((i) => i && i >= pageCount - 7)
  } else result = pageCountArray.filter((i) => i && i >= left && i < right)

  if (result.length > 1) {
    // Add first page and dots
    if (result[0] > 1) {
      if (result[0] > 2) {
        result.unshift('...')
      }
      result.unshift(1)
    }

    // Add dots and last page
    if (result[result.length - 1] < pageCount) {
      if (result[result.length - 1] !== pageCount - 1) {
        result.push('...')
      }
      result.push(pageCount)
    }
  }

  return result
}

export const Pagination = ({
  className,
  hitCount,
  totalPageCount,
  currentPage = 1,
  hitsPerPage,
  location,
  classifiedActions,
  showRefreshButton,
  device,
}) => {
  const buttonCount = totalPageCount > 5 ? 5 : totalPageCount
  const results = pagination(currentPage, totalPageCount)

  const [startIndex, endIndex] = paginationWindow(
    currentPage,
    totalPageCount,
    Math.min(totalPageCount, buttonCount),
  )

  const query = queryString.parse(location.search)

  const linkToPage = (page) => {
    let search = query
    if (page === 1) {
      // page=1 doesn't add anything for SEO purposes,
      // it'll just be duplicate content in the eyes of google
      delete search.page
    } else {
      search.page = page
    }
    search = queryString.stringify(search)

    if (page < 1 || page > totalPageCount) {
      return null
    }

    return {
      pathname: location.pathname,
      search,
    }
  }

  const clearAds = (e) => {
    e.persist()
    if (classifiedActions) {
      classifiedActions.clearAds()
    }
  }

  const desktopButtons = map(results, (result, i) => {
    const onClickHandler = result !== currentPage ? clearAds : () => {}
    return (
      <Button
        key={i}
        selected={result === currentPage}
        to={typeof result === 'number' && linkToPage(result)}
        onClick={onClickHandler}
      >
        {result}
      </Button>
    )
  })

  const tabletButtons = Array.from({ length: endIndex - startIndex }, (_, i) => {
    const page = startIndex + i + 1
    const onClickHandler = page !== currentPage ? clearAds : () => {}

    return (
      <Button
        key={page}
        selected={page === currentPage}
        to={linkToPage(page)}
        onClick={onClickHandler}
      >
        {page}
      </Button>
    )
  })

  const pageFrom = (currentPage - 1) * hitsPerPage + 1
  const pageTo = Math.min(currentPage * hitsPerPage, hitCount)

  const prevPage = currentPage - 1
  const nextPage = currentPage + 1

  const prevPageLink = linkToPage(prevPage)
  const nextPageLink = linkToPage(nextPage)
  const refreshPageLink = linkToPage(1)

  const refreshClick = (e) => {
    e.persist()
    classifiedActions.fetchAds(query, true)
    window.scrollTo(0, 0)
    trackEvent({
      eventType: 'Engagement',
      eventCategory: 'refreshButton',
      eventAction: 'on click fetch new ads with same search criteria',
      eventLabel: 'Nya annonser',
    })
  }

  return (
    <div className={className}>
      {totalPageCount > 1 && (
        <>
          <TextBody>
            <Strong>
              {pageFrom} – {pageTo}
            </Strong>
            &nbsp; av totalt <Strong>{numberGroup(hitCount)}</Strong> annonser
          </TextBody>

          <Wrapper>
            <PrevNextButton
              rel={prevPageLink && 'prev'}
              to={prevPageLink}
              onClick={prevPageLink && clearAds}
            >
              <IconLeft width={22} height={22} />
            </PrevNextButton>

            <Buttons>{device.is('large') ? desktopButtons : tabletButtons}</Buttons>

            <PrevNextButton
              rel={nextPageLink && 'next'}
              to={nextPageLink}
              onClick={nextPageLink && clearAds}
            >
              <IconRight width={22} height={22} />
            </PrevNextButton>
          </Wrapper>
        </>
      )}
      {showRefreshButton && (
        <RefreshWrapper>
          <RefreshButton rel="refresh" to={refreshPageLink} onClick={refreshClick}>
            <IconReload color="#363433" width={20} height={20} />
            <HelpLabel>Nya annonser</HelpLabel>
          </RefreshButton>
        </RefreshWrapper>
      )}
    </div>
  )
}

Pagination.propTypes = {
  hitCount: PropTypes.number,
  totalPageCount: PropTypes.number,
  currentPage: PropTypes.number,
  hitsPerPage: PropTypes.number,
  location: PropTypes.object.isRequired,
  className: PropTypes.string,
  classifiedActions: PropTypes.object,
  showRefreshButton: PropTypes.bool,
  device: PropTypes.object.isRequired,
}

export default withRouter(withDevice(Pagination))
